import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { PlaybookList } from '../head/HeadR2Pages';
import { TeacherFrame } from './TeacherFrame';
import { errorText, rpc, type AppError } from './api';
import type { Loc } from '../../lib/loc';
import type { Evidence } from '../r3/common';

type Item = { key: string; topic: string; title: Loc; body: Loc; is_draft: boolean; school_version: boolean; evidence?: Evidence };
const TOPICS = ['schoolwork', 'friends', 'family', 'sleep', 'health', 'exams', 'other', 'any'];

// I3: the school's playbook; with the premium option, teachers propose a
// school version of an activity (or a new one) for the head to approve.
export function PlaybookPage() {
  const { t } = useI18n();
  return (
    <TeacherFrame title={t['h2.playbookTitle']} active="playbook">
      {() => <Body />}
    </TeacherFrame>
  );
}

function Body() {
  const { t, lang } = useI18n();
  const announce = useAnnounce();
  const [data, setData] = useState<{ custom_allowed: boolean; items: Item[] } | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const [key, setKey] = useState('');
  const [topic, setTopic] = useState('exams');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const load = useCallback(async () => {
    const r = await rpc<NonNullable<typeof data>>('teacher_playbook');
    if (r.error) setErr(r.error);
    else setData(r.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function propose(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const k = key || `school_${Date.now().toString(36)}`;
    const r = await rpc('teacher_propose_playbook', { p_key: k, p_topic: topic, p_title: { [lang]: title }, p_body: { [lang]: body } });
    if (r.error) return setErr(r.error);
    announce(t['pb.proposed']);
    setTitle('');
    setBody('');
    setKey('');
  }

  if (err && !data) return <p className="notice notice-block" role="alert">{errorText(t, err)}</p>;
  if (!data) return <p role="status">{t.loading}</p>;
  return (
    <>
      <p className="notice notice-block">{data.custom_allowed ? t['pb.customOn'] : t['pb.customOff']}</p>
      {data.custom_allowed && (
        <section className="panel p5" aria-labelledby="pb-new">
          <h2 className="panel-title" id="pb-new">{t['pb.proposeTitle']}</h2>
          <form className="form" onSubmit={propose} noValidate>
            <div className="form-row">
              <div className="field-group">
                <label className="field-label" htmlFor="pb-base">{t['pb.base']}</label>
                <div className="select-wrap">
                  <select id="pb-base" className="select" value={key} onChange={(e) => setKey(e.target.value)}>
                    <option value="">{t['pb.newItem']}</option>
                    {data.items.map((i) => <option key={i.key} value={i.key}>{i.title[lang] || i.title.en}</option>)}
                  </select>
                </div>
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor="pb-topic">{t['pb.topic']}</label>
                <div className="select-wrap">
                  <select id="pb-topic" className="select" value={topic} onChange={(e) => setTopic(e.target.value)}>
                    {TOPICS.map((x) => <option key={x} value={x}>{x === 'any' ? t['h2.pbAnyTopic'] : t[`topic.${x}` as 'topic.exams']}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor="pb-title">{t['pb.titleLabel']}</label>
              <input id="pb-title" className="field" maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor="pb-body">{t['pb.bodyLabel']}</label>
              <textarea id="pb-body" className="field" rows={3} maxLength={600} value={body} onChange={(e) => setBody(e.target.value)} aria-describedby="pb-hint" />
              <p className="form-hint" id="pb-hint">{t['pb.rule']}</p>
            </div>
            {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
            <div className="form-actions"><button type="submit" className="btn btn-primary" disabled={title.trim().length < 3}>{t['pb.send']}</button></div>
          </form>
        </section>
      )}
      <section className="panel p5" aria-labelledby="pb-all">
        <h2 className="panel-title" id="pb-all">{t['h2.pbItems']}</h2>
        <PlaybookList items={data.items} />
      </section>
    </>
  );
}
